import i18next from 'i18next';
import { navigate } from '@dvag/dfs-orchestrator-client/messengers';
import { appNames } from '@dvag/dfs-constant-config/app-names';
import { dashboardUtil } from 'service/model/dashboardUtil';
import { handleNavigateToPath } from 'service/handleNavigateToPath';
import { useUtil } from 'screen/HouseholdDashboard/component/BottomCard/util';
import { checkIcon } from 'utils/util';
import { buildPath } from 'service/buildPath';
import type { SectionListData } from 'type/sectionList';
import type { PersonInHouseholdDto } from 'graphql/person/generates';
import type { KundanzuganHaushaltDetailResponse } from 'type/twoFactor';

interface DataInterface {
  personId: string;
  status: string;
}

const { PERSONAL_DATA, PRIVACY_POLICY, CUSTOMER_ACCESS, ADVISOR } = dashboardUtil;

export const FEEDBACK_COLLABORATION_SECTION = 'feedback-zusammenarbeit';
export const DREAM_JOB_SECTION = 'traumberuf';

const FEEDBACK_BERATUNGS_PATH = '/terminabschluss/bewertung-davor';
const FEEDBACK_DIREKT_PATH = '/weiterempfehlung';
const FEEDBACK_PRAS_PATH = '/empfehlungprasentation';

const DREAMJOB_CHANCEN_PATH = '/terminabschluss/empfehlung-und-anwerbung/werben';
const DREAMJOB_PRAS_PATH = '/teamaufbauprasentation';
const DREAMJOB_DIREKT_PATH = '/teamaufbau';

const sectionBase = (householdId?: string, dashboard?: string) =>
  `/haushalt/${householdId}/${dashboard ?? 'analyse'}/sektion`;

export const useGetRootSections = (
  householdId?: string,
  customerPortalData?: KundanzuganHaushaltDetailResponse[],
  dashboard?: string,
  personList?: PersonInHouseholdDto[],
  personListWithConsent?: DataInterface[],
): SectionListData[] => {
  const { openKundenzugangTool } = useUtil(householdId ?? '');
  const base = sectionBase(householdId, dashboard);

  const consentedCount =
    customerPortalData?.filter((person) => person.registrierungsstatus === 'VOLLSTAENDIG').length ??
    0;
  const totalCount = customerPortalData?.length ?? 0;
  const consentCountText = totalCount > 0 ? ` (${consentedCount}/${totalCount})` : '';
  const customerPortalTitle = `${i18next.t('dashboardCards.collaboration.customerPortal')}${consentCountText}`;

  const consentCount =
    personListWithConsent?.length !== undefined && personList?.length
      ? `(${personListWithConsent?.length}/${personList?.length})`
      : '';
  const privacyTitle = consentCount
    ? `${i18next.t('dashboard.bottomList.gdpr')} ${consentCount}`
    : i18next.t('dashboard.bottomList.gdpr');

  const sections: SectionListData[] = [
    {
      icon: checkIcon('ausweis-check'),
      id: PERSONAL_DATA,
      path: PERSONAL_DATA,
      title: i18next.t('dashboardCards.collaboration.personalData'),
      onClick: () => handleNavigateToPath(`${base}/${PERSONAL_DATA}`, '', ADVISOR),
    },
    {
      icon: 'schild',
      id: PRIVACY_POLICY,
      path: PRIVACY_POLICY,
      title: privacyTitle,
      onClick: () => handleNavigateToPath(`${base}/${PRIVACY_POLICY}`, '', ADVISOR),
    },
  ];

  sections.push({
    icon: checkIcon('partnerschaft-kunde'),
    id: CUSTOMER_ACCESS,
    path: CUSTOMER_ACCESS,
    title: customerPortalTitle,
    onClick: openKundenzugangTool,
  });

  sections.push({
    icon: checkIcon('daumen-hoch'),
    id: FEEDBACK_COLLABORATION_SECTION,
    path: FEEDBACK_COLLABORATION_SECTION,
    title: i18next.t('dashboardCards.collaboration.feedbackAndCollaboration'),
    onClick: () => handleNavigateToPath(`${base}/${FEEDBACK_COLLABORATION_SECTION}`, '', ADVISOR),
  });

  sections.push({
    icon: checkIcon('karriereleiter'),
    id: DREAM_JOB_SECTION,
    path: DREAM_JOB_SECTION,
    title: i18next.t('dashboardCards.collaboration.dreamJob'),
    onClick: () => handleNavigateToPath(`${base}/${DREAM_JOB_SECTION}`, '', ADVISOR),
  });

  return sections;
};

export const useGetFeedbackCollaborationList = (householdId?: string): SectionListData[] => {
  const advisoryFeedbackUrls = {
    url: `/${appNames.advisor}${buildPath(
      `/haushalt/:householdId/analyse${FEEDBACK_BERATUNGS_PATH}`,
      {
        householdId: householdId as string,
      },
    )}`,
    nextUrl: `/berater${window.location.pathname}`,
    resetWorkflow: true,
  };

  const directFeedbackUrls = {
    url: `/${appNames.advisor}${buildPath('/haushalt/:householdId/weiterempfehlung', {
      householdId: householdId as string,
    })}`,
    nextUrl: `/${appNames.advisor}${buildPath('/haushalt/:householdId/analyse', {
      householdId: householdId as string,
    })}`,
  };

  const list: SectionListData[] = [
    {
      id: 'beratungsfeedback',
      path: FEEDBACK_BERATUNGS_PATH,
      title: i18next.t(
        'dashboardCards.collaboration.subsections.feedbackAndCollaboration.advisoryFeedback',
      ),
      icon: checkIcon('lachen'),
      onClick: () =>
        navigate(
          advisoryFeedbackUrls.url,
          advisoryFeedbackUrls.nextUrl,
          advisoryFeedbackUrls.resetWorkflow,
        ),
    },
  ];

  list.push(
    {
      id: 'kunden-werben-kunden-praesentation',
      path: FEEDBACK_PRAS_PATH,
      title: i18next.t(
        'dashboardCards.collaboration.subsections.feedbackAndCollaboration.clientsRecruitClientsPresentation',
      ),
      icon: checkIcon('slideshow'),
      onClick: () =>
        handleNavigateToPath(`/haushalt/${householdId}${FEEDBACK_PRAS_PATH}`, '', ADVISOR),
    },
    {
      id: 'kunden-werben-kunden-direkteinstieg',
      path: FEEDBACK_DIREKT_PATH,
      title: i18next.t(
        'dashboardCards.collaboration.subsections.feedbackAndCollaboration.clientsRecruitClientsDirect',
      ),
      icon: checkIcon('vorspulen'),
      onClick: () => navigate(directFeedbackUrls.url, directFeedbackUrls.nextUrl),
    },
  );

  return list;
};

export const useGetDreamJobList = (householdId?: string): SectionListData[] => {
  const chancesDreamJobUrls = {
    url: `/${appNames.advisor}${buildPath(
      `/haushalt/:householdId/analyse${DREAMJOB_CHANCEN_PATH}`,
      {
        householdId: householdId as string,
      },
    )}`,
    nextUrl: `/berater${window.location.pathname}`,
    resetWorkflow: true,
  };

  const directDreamJobUrls = {
    url: `/${appNames.advisor}${buildPath('/haushalt/:householdId/teamaufbau', {
      householdId: householdId as string,
    })}`,
    nextUrl: `/${appNames.advisor}${buildPath('/haushalt/:householdId/analyse', {
      householdId: householdId as string,
    })}`,
  };

  return [
    {
      id: 'chancen-moeglichkeiten',
      path: DREAMJOB_CHANCEN_PATH,
      title: i18next.t(
        'dashboardCards.collaboration.subsections.dreamJob.opportunitiesAndPossibilities',
      ),
      icon: checkIcon('karriereleiter'),
      onClick: () =>
        navigate(
          chancesDreamJobUrls.url,
          chancesDreamJobUrls.nextUrl,
          chancesDreamJobUrls.resetWorkflow,
        ),
    },
    {
      id: 'traumberuf-praesentation',
      path: DREAMJOB_PRAS_PATH,
      title: i18next.t('dashboardCards.collaboration.subsections.dreamJob.dreamJobPresentation'),
      icon: checkIcon('slideshow'),

      onClick: () =>
        handleNavigateToPath(`/haushalt/${householdId}${DREAMJOB_PRAS_PATH}`, '', ADVISOR),
    },
    {
      id: 'traumberuf-direkteinstieg',
      path: DREAMJOB_DIREKT_PATH,
      title: i18next.t('dashboardCards.collaboration.subsections.dreamJob.dreamJobDirect'),
      icon: checkIcon('vorspulen'),
      onClick: () => navigate(directDreamJobUrls.url, directDreamJobUrls.nextUrl),
    },
  ];
};
